import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Download, ShieldCheck, Link as LinkIcon } from "lucide-react";
import { STATUSES } from "@/lib/constants";

const CSV_COLUMNS = [
  { key: (a) => `${a.first_name} ${a.last_name}`, label: "Name" },
  { key: (a) => a.email, label: "Email" },
  { key: (a) => a.phone, label: "Phone" },
  { key: (a) => a.advance_amount_requested, label: "Requested Advance" },
  { key: (a) => a.net_commission_amount, label: "Net Commission" },
  { key: (a) => a.expected_closing_date, label: "Closing Date" },
  { key: (a) => a.status, label: "Status" },
  { key: (a) => new Date(a.submitted_at).toISOString(), label: "Submitted At" },
  { key: (a) => a.brokerage_name || "", label: "Brokerage" },
  { key: (a) => a.state_licensed_in || "", label: "State" },
  { key: (a) => a.source || "direct", label: "Source" },
];

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

function downloadCsv(rows) {
  const header = CSV_COLUMNS.map((c) => csvEscape(c.label)).join(",");
  const body = rows
    .map((r) => CSV_COLUMNS.map((c) => csvEscape(c.key(r))).join(","))
    .join("\n");
  const csv = `${header}\n${body}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const ts = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `hannah-howard-applications-${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function AdminFilters({ search, setSearch, filter, setFilter }) {
  return (
    <div className="flex gap-3 w-full sm:w-auto">
      <Input placeholder="Search name or email..." value={search} onChange={(e) => setSearch(e.target.value)} className="sm:w-72" data-testid="admin-search"/>
      <Select value={filter} onValueChange={setFilter}>
        <SelectTrigger className="sm:w-48" data-testid="admin-filter"><SelectValue placeholder="Filter status"/></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  );
}

function AdminTableRow({ app, onStatusChange, onViewConsents, onCopyBrokerLink }) {
  return (
    <TableRow data-testid={`admin-row-${app.id}`}>
      <TableCell className="font-medium">{app.first_name} {app.last_name}</TableCell>
      <TableCell className="text-[#5A3B31]">{app.email}</TableCell>
      <TableCell>{app.phone}</TableCell>
      <TableCell>${Number(app.advance_amount_requested).toLocaleString()}</TableCell>
      <TableCell>${Number(app.net_commission_amount).toLocaleString()}</TableCell>
      <TableCell>{app.expected_closing_date}</TableCell>
      <TableCell>
        <Select value={app.status} onValueChange={(v) => onStatusChange(app.id, v)}>
          <SelectTrigger className="w-40" data-testid={`admin-status-${app.id}`}><SelectValue/></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="text-xs text-[#8C7A70]">{new Date(app.submitted_at).toLocaleString()}</TableCell>
      <TableCell>
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => onViewConsents(app.id)}
            data-testid={`admin-view-consents-${app.id}`}
            className="text-xs uppercase tracking-wider text-[#5A3B31] hover:text-[#2A1C14] inline-flex items-center gap-1.5"
            title="View consent history"
          >
            <ShieldCheck size={12} strokeWidth={1.5} /> Consents
          </button>
          <button
            type="button"
            onClick={() => onCopyBrokerLink(app.id)}
            data-testid={`admin-copy-broker-${app.id}`}
            className="text-xs uppercase tracking-wider text-[#5A3B31] hover:text-[#2A1C14] inline-flex items-center gap-1.5"
            title="Copy broker verification link to clipboard"
          >
            <LinkIcon size={12} strokeWidth={1.5} /> Broker Link
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function AdminTable({
  loading, search, setSearch, filter, setFilter, visible, onStatusChange, onViewConsents, onCopyBrokerLink,
}) {
  const canExport = !loading && visible.length > 0;
  const onExport = () => {
    if (!canExport) return;
    downloadCsv(visible);
  };
  return (
    <>
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-8">
        <div>
          <span className="overline">Applications</span>
          <h1 className="font-serif-lux text-4xl mt-2">All Advance Requests</h1>
          <p className="text-xs text-[#8C7A70] mt-2">
            Showing {visible.length} {visible.length === 1 ? "result" : "results"}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <AdminFilters search={search} setSearch={setSearch} filter={filter} setFilter={setFilter}/>
          <button
            type="button"
            onClick={onExport}
            disabled={!canExport}
            data-testid="admin-export-csv"
            className="btn-blush text-sm py-2 px-4 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <Download size={14} strokeWidth={1.5} /> Export CSV
          </button>
        </div>
      </div>

      <div className="lux-card overflow-hidden">
        <Table data-testid="admin-table">
          <TableHeader>
            <TableRow className="bg-[#F4EEE8] hover:bg-[#F4EEE8]">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Requested Advance</TableHead>
              <TableHead>Net Commission</TableHead>
              <TableHead>Closing Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Audit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (<TableRow><TableCell colSpan={9} className="text-center py-10 text-[#8C7A70]">Loading...</TableCell></TableRow>)}
            {!loading && visible.length === 0 && (<TableRow><TableCell colSpan={9} className="text-center py-10 text-[#8C7A70]">No applications yet.</TableCell></TableRow>)}
            {visible.map((a) => (<AdminTableRow key={a.id} app={a} onStatusChange={onStatusChange} onViewConsents={onViewConsents} onCopyBrokerLink={onCopyBrokerLink} />))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
